/**
 * "카카오톡으로 공유" — ③B (docs/OZK_FINAL_LAUNCH_STRATEGY.md 16장 ③B).
 *
 * This is NOT the same button as HelpRequestButton's "카톡으로 물어보기" (③A).
 * ③A creates a help_request and is about connecting the user's own question
 * to a business. ③B is pure content-sharing for new-user acquisition: it
 * shares a link to the content itself (Topic/article/business/market
 * post/question·experience/meeting) via Kakao Talk, with no server-side
 * effect of its own.
 *
 * Kakao's JS SDK (`Kakao.Share.sendDefault`) never reports back whether the
 * user actually completed a send inside the Kakao Talk share sheet, so this
 * only ever tracks "share sheet opened", not "share completed" - the 16장
 * "카카오 공유 완료" funnel step depends on future Kakao-side reporting we
 * don't have. What we can honestly measure now: click -> sheet opened
 * (or fallback used) -> (later) link visits via the utm_source=kakao_share
 * param this component appends.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { trackEvent, type AnalyticsEvent } from "@/lib/analytics";
import { initKakao } from "@/lib/firebase";
import { Share2 } from "lucide-react";

export type ShareContentType =
  | "topic"
  | "article"
  | "business"
  | "market"
  | "community_talk"
  | "meeting";

interface ShareKakaoButtonProps {
  /** Relative path to the content, e.g. `/topics/${id}` */
  path: string;
  contentType: ShareContentType;
  contentId?: string;
  title: string;
  description?: string;
  imageUrl?: string;
  /** Feed template CTA button label, default "자세히 보기" */
  ctaLabel?: string;
  variant?: "default" | "outline" | "ghost" | "secondary";
  size?: "sm" | "default" | "lg" | "icon";
  className?: string;
  label?: string;
}

function buildShareUrl(path: string, contentType: ShareContentType): string {
  if (typeof window === "undefined") return path;
  const url = new URL(path, window.location.origin);
  url.searchParams.set("utm_source", "kakao_share");
  url.searchParams.set("utm_medium", "share");
  url.searchParams.set("utm_content", contentType);
  return url.toString();
}

export function ShareKakaoButton({
  path,
  contentType,
  contentId,
  title,
  description,
  imageUrl,
  ctaLabel = "자세히 보기",
  variant = "outline",
  size = "sm",
  className,
  label = "카카오톡으로 공유",
}: ShareKakaoButtonProps) {
  const { toast } = useToast();
  const [sharing, setSharing] = useState(false);

  const handleShare = async () => {
    if (sharing) return;
    setSharing(true);
    const shareUrl = buildShareUrl(path, contentType);

    trackEvent({
      event: "kakao_share" as AnalyticsEvent,
      contentType,
      contentId,
      metadata: { stage: "clicked" },
    });

    try {
      initKakao();
      if (window.Kakao?.isInitialized?.() && window.Kakao.Share?.sendDefault) {
        window.Kakao.Share.sendDefault({
          objectType: "feed",
          content: {
            title,
            description: description || "",
            imageUrl: imageUrl || `${window.location.origin}/og-image.jpg`,
            link: { mobileWebUrl: shareUrl, webUrl: shareUrl },
          },
          buttons: [
            {
              title: ctaLabel,
              link: { mobileWebUrl: shareUrl, webUrl: shareUrl },
            },
          ],
        });
        trackEvent({
          event: "kakao_share" as AnalyticsEvent,
          contentType,
          contentId,
          metadata: { stage: "share_sheet_opened" },
        });
        return;
      }
    } catch (error) {
      console.error("[ShareKakaoButton] Kakao.Share.sendDefault failed:", error);
    } finally {
      setSharing(false);
    }

    // Fallback when the Kakao JS key isn't configured or the SDK/Share module
    // isn't available - native share sheet, then clipboard, never a dead click.
    try {
      if (navigator.share) {
        await navigator.share({ title, text: description, url: shareUrl });
        trackEvent({
          event: "kakao_share" as AnalyticsEvent,
          contentType,
          contentId,
          metadata: { stage: "native_share_used" },
        });
        return;
      }
    } catch {
      // user cancelled the native share sheet - not an error worth surfacing
      return;
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({
        title: "링크가 복사되었습니다",
        description: "카카오톡 대화방에 붙여넣어 공유해보세요.",
      });
      trackEvent({
        event: "kakao_share" as AnalyticsEvent,
        contentType,
        contentId,
        metadata: { stage: "link_copied" },
      });
    } catch (error) {
      toast({
        title: "공유 링크 생성 실패",
        description: "잠시 후 다시 시도해주세요",
        variant: "destructive",
      });
    }
  };

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      className={className}
      onClick={handleShare}
      disabled={sharing}
      data-testid="button-kakao-share"
    >
      <Share2 className="mr-1.5 h-4 w-4" />
      {label}
    </Button>
  );
}
